import React from 'react';
import { FormattedMessage } from 'react-intl';
import { renderNumber } from '@share/utils';
import { NoImageSvg } from '@assets';
import { Currency, HotelStars } from '@components';
import { ICancelReservationHotelInfo } from '@share/common-types';
import { getFullAddress } from '@utils';

import './style.scss';

interface IProps {
  hotelDetails: ICancelReservationHotelInfo;
  isPromo: boolean;
}

const ZERO = 0;

export function ReservationCancellationRoomInfo(props: IProps): React.ReactElement {
  const { isPromo = false, hotelDetails } = props;
  const {
    hotel,
    activity,
    imageUrl,
    roomName,
    roomDescription,
    checkIn,
    checkOut,
    roomsCount,
    numberOfNights,
    totalAmount,
    rooms,
    priceDifference,
    discountAmount,
    bookingPrice,
    currency,
    bookingType,
    bookingWallet,
  } = hotelDetails;

  const mealBasisInfo = rooms[ZERO].basis;
  const mealBasisPrice = priceDifference;

  const isCars = bookingType === 'Car';
  const isActivity = bookingType === 'Activity';

  const bookingDetails: any = isActivity ? activity : hotel;

  return (
    <div className="reservation-cancellation-room-info">
      <div className="reservation-cancellation-room-info__wrapper">
        {imageUrl.length !== ZERO ? (
          <div className="reservation-cancellation-room-info__room-image">
            <img
              className="reservation-cancellation-room-info__room-img"
              src={imageUrl}
              alt={roomName}
            />
          </div>
        ) : (
          <div className="reservation-cancellation-room-info__room-img no-image">
            <NoImageSvg />
          </div>
        )}
        <div className="reservation-cancellation-room-info__content border-bm">
          <p className="reservation-cancellation-room-info__hotel-name secondary-font">{bookingDetails?.name}</p>
          {(!isActivity && !isCars) && (
            <div className="hotel-info__stars">
              <HotelStars stars={bookingDetails?.stars} />
            </div>)}

            {!isActivity && (
              <p className="room-info__address">
                {getFullAddress(bookingDetails?.address, bookingDetails?.city as string, bookingDetails?.state as string, bookingDetails?.zipCode, bookingDetails?.countryCode, bookingDetails?.countryName).join(', ')}
              </p>)}

          {!isActivity && (
            <div className="reservation-cancellation-room-info__room-info-block">
              <p className="reservation-cancellation-room-info__room-info-title">{roomName}</p>
              <p className="reservation-cancellation-room-info__room-info-description">
                {roomDescription}
              </p>
            </div>)}

          <div className="reservation-cancellation-room-info__period">
            <div className="reservation-cancellation-room-info__period-item">
              <p className="reservation-cancellation-room-info__period-item-title">
                <FormattedMessage id={isActivity ? 'activity.start.date' : isCars ? 'cars.pickup' : 'check.in'} />:
              </p>
              <p className="reservation-cancellation-room-info__period-item-description">
                {checkIn}
              </p>
            </div>
            {!isActivity && (
              <div className="reservation-cancellation-room-info__period-item">
                <p className="reservation-cancellation-room-info__period-item-title">
                  <FormattedMessage id={isCars ? 'cars.dropoff' : 'check.out'} />:
                </p>
                <p className="reservation-cancellation-room-info__period-item-description">
                  {checkOut}
                </p>
              </div>)}
          </div>
        </div>

        <div className="reservation-cancellation-room-info__content ">
          <h4 className="reservation-cancellation-room-info__price-title secondary-font">
            <FormattedMessage id="price.details" />
          </h4>
          <div className="reservation-cancellation-room-info__price-info">
            <p className="reservation-cancellation-room-info__price-info-text">
              {!isCars ? 
                !isActivity ? (
                  <>
                    {roomsCount} <FormattedMessage id="room" values={{ count: roomsCount }} /> x{' '}
                    <FormattedMessage id="nights" values={{ count: numberOfNights }} />
                  </>) : (
                  <FormattedMessage id="activity.price" />) : (
                <FormattedMessage id="car.price" values={{ days: numberOfNights, count: numberOfNights }} />
              )}
            </p>
            <p className="reservation-cancellation-room-info__price-info-text">
              <Currency currency={currency} />{renderNumber(totalAmount, 2)}
            </p>
          </div>

          {mealBasisPrice !== ZERO ? (
            <div className="reservation-cancellation-room-info__price-info">
              <p className="reservation-cancellation-room-info__price-meal-basis-text">
                {mealBasisInfo}
              </p>
              <p className="reservation-cancellation-room-info__price-meal-basis-text">
                <Currency currency={currency} />{renderNumber(mealBasisPrice, 2)}
              </p>
            </div>
          ) : null}

          {discountAmount > 0 ? (
            <>
              <div className="reservation-cancellation-room-info__price-info">
                <p className="reservation-cancellation-room-info__price-savings-text">
                  <FormattedMessage id={isPromo ? 'savings' : 'member.savings.label'} />
                </p>
                <p className="reservation-cancellation-room-info__price-savings-text">
                  -<Currency currency={currency} /> {renderNumber(discountAmount, 2)}
                </p>
              </div>
            </>
          ) : null}

          {(bookingWallet && bookingWallet.walletClientCashUsed > 0) ? (
            <div className="room-info__price-info">
              <p className="room-info__price-client-cash-text" style={{ color: '#0d99d6' }}>
                <FormattedMessage id="wallet.client.cash.applied" values={{ clientCash: bookingWallet?.walletPointsUsed, clientCashName: bookingWallet?.walletClientCashName }} />
              </p>
              <p className="room-info__price-client-cash-text">
                <Currency currency={currency} />{renderNumber(bookingWallet?.walletClientCashUsed, 2)}
              </p>
            </div>
          ) : null}
        </div>
        <div className="reservation-cancellation-room-info__content border-bm pb-0 mt-0"></div>
        <div className="reservation-cancellation-room-info__content">
          <div className="reservation-cancellation-room-info__total-price-info-wrapper">
            <div className="reservation-cancellation-room-info__total-price-info">
              <p className="reservation-cancellation-room-info__price-info-text">
                <FormattedMessage id="total.pay" />:
              </p>
            </div>
            <p className="reservation-cancellation-room-info__price-info-amount">
              <Currency currency={currency} />{renderNumber(bookingPrice, 2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
